import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fmtT } from "./lib";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Loader2, FileSignature, Scale, CheckCircle2, Upload, Wheat,
} from "lucide-react";
import { toast } from "sonner";

export default function GranosImportar({ onDone }) {
  const [cultivo, setCultivo] = useState("__auto__");
  const [fileA, setFileA] = useState(null);
  const [fileB, setFileB] = useState(null);
  const [busy, setBusy] = useState(false);
  const [review, setReview] = useState(null);

  const reset = () => { setFileA(null); setFileB(null); setReview(null); setCultivo("__auto__"); };

  const call = async (confirm) => {
    if (!fileA && !fileB) { toast.error("Subí al menos una planilla."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      if (fileA) fd.append("file_a", fileA);
      if (fileB) fd.append("file_b", fileB);
      if (cultivo !== "__auto__") fd.append("cultivo", cultivo);
      fd.append("confirm", confirm ? "true" : "false");
      const res = await api.post("/granos/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (confirm) { toast.success(res.data.message || "Importado"); reset(); onDone?.(); }
      else setReview(res.data.review);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo importar");
    } finally { setBusy(false); }
  };

  const FileRow = ({ file, setFile, icon: Icon, label, testid }) => (
    <label className="flex items-center gap-3 rounded-xl border p-3.5 text-sm cursor-pointer active:bg-slate-50">
      <Icon className="h-5 w-5 text-[#0f5233] shrink-0" />
      <span className="flex-1 truncate">{file ? file.name : label}</span>
      {file && <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />}
      <input type="file" accept=".xls,.xlsx,.xltx" className="hidden" data-testid={testid} onChange={(e) => setFile(e.target.files?.[0] || null)} />
    </label>
  );

  return (
    <div className="p-4 space-y-4" data-testid="granos-importar-tab">
      <div className="rounded-2xl border bg-white p-4 space-y-4">
        <div className="flex items-center gap-2 text-[#0f5233]">
          <Wheat className="h-5 w-5" /> <span className="font-semibold">Importar contratos de granos</span>
        </div>
        <p className="text-[12px] text-slate-500">Subí el Listado de Contratos (A) y el Análisis por Depósito (B) de un mismo cultivo y zafra.</p>

        <div>
          <label className="text-xs font-semibold text-slate-500">Cultivo</label>
          <Select value={cultivo} onValueChange={setCultivo}>
            <SelectTrigger className="mt-1 h-11" data-testid="granos-import-cultivo"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__auto__">Detectar automáticamente</SelectItem>
              <SelectItem value="Soja">Soja</SelectItem>
              <SelectItem value="Maíz">Maíz</SelectItem>
              <SelectItem value="Trigo">Trigo</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <FileRow file={fileA} setFile={setFileA} icon={FileSignature} label="Reporte A · Listado de Contratos" testid="granos-import-file-a" />
        <FileRow file={fileB} setFile={setFileB} icon={Scale} label="Reporte B · Análisis por Depósito" testid="granos-import-file-b" />

        {review && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-3 text-sm space-y-1" data-testid="granos-import-review">
            <p className="font-semibold text-[#0f5233]">Revisión — {review.cultivo} {review.zafra}</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-[13px]">
              <span>Contratos: <b>{review.total_contratos}</b></span>
              <span>En ambos: <b>{review.en_ambos}</b></span>
              <span>Ton. A: <b>{fmtT(review.ton_listado_a)}</b></span>
              <span>Ton. B: <b>{fmtT(review.ton_analisis_b)}</b></span>
              <span>Sin firmar: <b>{review.sin_firmar}</b></span>
              <span>Atrasados: <b>{review.atrasados}</b></span>
            </div>
            {review.control_alerta?.length > 0 && (
              <p className="text-amber-700 text-xs">⚠️ Control no cierra: {review.control_alerta.join(", ")}</p>
            )}
          </div>
        )}

        {!review ? (
          <Button onClick={() => call(false)} disabled={busy} className="w-full h-11 gap-2 bg-[#0f5233] hover:bg-[#0b3f27]" data-testid="granos-import-review-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Revisar
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button variant="outline" onClick={reset} disabled={busy} className="flex-1 h-11">Cancelar</Button>
            <Button onClick={() => call(true)} disabled={busy} className="flex-1 h-11 gap-2 bg-[#0f5233] hover:bg-[#0b3f27]" data-testid="granos-import-confirm-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Confirmar
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
